import { authorizedFetch, json } from '@nimbitlabs/ui';
import type * as API from '@/types/api';
import { ProductDto } from '@/types/api';

export const api = {
  // Account endpoints
  account: {
    get: async (): Promise<API.GetUserAccountResponseDto> => {
      return json(await authorizedFetch('/accounts/current', {}, false, false)); // Requires account header
    },
    create: async (data: API.CreateAccountDto): Promise<API.AccountDto> => {
      return json(await authorizedFetch('/accounts', {
        method: 'POST',
        body: JSON.stringify(data),
      }, true, false)); // Skip account header (no account yet), but include auth header
    },
    update: async (data: API.UpdateAccountDto): Promise<API.AccountDto> => {
      return json(await authorizedFetch('/accounts', {
        method: 'PUT',
        body: JSON.stringify(data),
      }));
    },
    updateReferralCode: async (data: API.UpdateReferralCodeDto): Promise<API.AccountDto> => {
      return json(await authorizedFetch('/accounts/referral-code', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }));
    },
  },

  // Accounts endpoints (plural - for multi-account management)
  accounts: {
    getAll: async (options?: { silent?: boolean }): Promise<API.GetUserAccountsResponseDto> => {
      return json(await authorizedFetch('/accounts', {}, true, false), options); // Skip account header (lists all user's accounts), but include auth header
    },
  },

  accountUsers: {
    list: async (): Promise<API.UserDto[]> => {
      return json(await authorizedFetch('/users'));
    },
    invite: async (user: API.InviteUserDto): Promise<{ message: string; email: string; cognitoUsername: string; isNewUser: boolean; expiresAt: string }> => {
      return json(await authorizedFetch('/users/invite', {
        method: 'POST',
        body: JSON.stringify(user),
      }));
    },
    update: async (userId: string, data: { name?: string; role?: string; department?: string; status?: string }): Promise<API.UserDto> => {
      return json(await authorizedFetch(`/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }));
    },
    getPendingInvitations: async (): Promise<API.PendingInvitationDto[]> => {
      return json(await authorizedFetch('/users/pending-invitations'));
    },
    resendInvitation: async (invitationId: string): Promise<{ message: string }> => {
      return json(await authorizedFetch(`/users/pending-invitations/${invitationId}/resend`, {
        method: 'POST',
      }));
    },
    cancelInvitation: async (invitationId: string): Promise<{ message: string; userDeleted: boolean }> => {
      return json(await authorizedFetch(`/users/pending-invitations/${invitationId}`, {
        method: 'DELETE',
      }));
    },
    remove: async (userId: string): Promise<void> => {
      await authorizedFetch(`/users/${userId}`, {
        method: 'DELETE',
      });
    },
  },

  // Current user endpoints
  user: {
    updateProfile: async (data: { name?: string }): Promise<void> => {
      await authorizedFetch('/users/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
  },

  // Legacy user endpoints (for backward compatibility)
  users: {
    getAll: async (): Promise<API.UserDto[]> => {
      return api.accountUsers.list();
    },
    getById: async (id: string): Promise<API.UserDto> => {
      const users = await api.accountUsers.list();
      const user = users.find(u => u.id === id);
      if (!user) throw new Error('User not found');
      return user;
    },
    create: async (data: { email: string; role: API.UserRole }): Promise<API.UserDto> => {
      await api.accountUsers.invite({
        email: data.email,
        name: null,
        role: data.role,
        department: null
      });
      const users = await api.accountUsers.list();
      return users[users.length - 1];
    },
    update: async (id: string, data: { role?: API.UserDto['role'] }): Promise<API.UserDto> => {
      // Note: Update not directly supported in new API
      throw new Error('Update user not supported - use remove and re-invite');
    },
    delete: async (id: string): Promise<void> => {
      return api.accountUsers.remove(id);
    },
  },

  teams: {
    getAll: async (): Promise<API.TeamDto[]> => {
      return json(await authorizedFetch('/teams'));
    },
    getById: async (id: string): Promise<API.TeamDto> => {
      return json(await authorizedFetch(`/teams/${id}`));
    },
    create: async (data: API.CreateTeamDto): Promise<API.TeamDto> => {
      return json(await authorizedFetch('/teams', {
        method: 'POST',
        body: JSON.stringify(data),
      }));
    },
    update: async (id: string, data: API.UpdateTeamDto): Promise<API.TeamDto> => {
      return json(await authorizedFetch(`/teams/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }));
    },
    delete: async (id: string): Promise<void> => {
      await authorizedFetch(`/teams/${id}`, { method: 'DELETE' });
    },
    getServices: async (id: string): Promise<API.ServiceDto[]> => {
      return json(await authorizedFetch(`/teams/${id}/services`));
    },
    addMember: async (teamId: string, userId: string, role: string): Promise<void> => {
      await authorizedFetch(`/teams/${teamId}/members`, {
        method: 'POST',
        body: JSON.stringify({ userId, role }),
      });
    },
    removeMember: async (teamId: string, userId: string): Promise<void> => {
      await authorizedFetch(`/teams/${teamId}/members/${userId}`, {
        method: 'DELETE',
      });
    },
  },

  services: {
    getAll: async (): Promise<API.ServiceDto[]> => {
      return json(await authorizedFetch('/services'));
    },
    getById: async (id: string, options?: { includeEnvironments?: boolean }): Promise<API.ServiceDto> => {
      const params = new URLSearchParams();
      if (options?.includeEnvironments) params.set('includeEnvironments', 'true');
      const url = `/services/${id}${params.toString() ? `?${params.toString()}` : ''}`;
      return json(await authorizedFetch(url));
    },
    create: async (data: API.CreateServiceDto): Promise<API.ServiceDto> => {
      return json(await authorizedFetch('/services', {
        method: 'POST',
        body: JSON.stringify(data),
      }));
    },
    update: async (id: string, data: API.UpdateServiceDto): Promise<API.ServiceDto> => {
      return json(await authorizedFetch(`/services/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }));
    },
    updateRoot: async (id: string, data: API.UpdateServiceRootDto): Promise<API.ServiceDto> => {
      return json(await authorizedFetch(`/services/${id}/root`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }));
    },
    delete: async (id: string): Promise<void> => {
      await authorizedFetch(`/services/${id}`, { method: 'DELETE' });
    },
    getDeployments: async (id: string): Promise<API.DeploymentDto[]> => {
      return json(await authorizedFetch(`/services/${id}/deployments`));
    },
  },

  environments: {
    getAll: async (): Promise<API.EnvironmentDto[]> => {
      return json(await authorizedFetch('/environments'));
    },
    getById: async (id: string): Promise<API.EnvironmentDto> => {
      return json(await authorizedFetch(`/environments/${id}`));
    },
    create: async (data: API.CreateEnvironmentDto): Promise<API.EnvironmentDto> => {
      return json(await authorizedFetch('/environments', {
        method: 'POST',
        body: JSON.stringify(data),
      }));
    },
    update: async (id: string, data: API.UpdateEnvironmentDto): Promise<API.EnvironmentDto> => {
      return json(await authorizedFetch(`/environments/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }));
    },
    delete: async (id: string): Promise<void> => {
      await authorizedFetch(`/environments/${id}`, { method: 'DELETE' });
    },
    getDeployments: async (id: string): Promise<API.DeploymentDto[]> => {
      return json(await authorizedFetch(`/environments/${id}/deployments`));
    },
  },

  serviceEnvironments: {
    getAll: async (): Promise<API.ServiceEnvironmentDto[]> => {
      return json(await authorizedFetch('/service-environments'));
    },
    getById: async (id: string): Promise<API.ServiceEnvironmentDto> => {
      return json(await authorizedFetch(`/service-environments/${id}`));
    },
    getByServiceId: async (serviceId: string): Promise<API.ServiceEnvironmentDto[]> => {
      return json(await authorizedFetch(`/service-environments/service/${serviceId}`));
    },
    create: async (data: API.CreateServiceEnvironmentDto): Promise<API.ServiceEnvironmentDto> => {
      return json(await authorizedFetch('/service-environments', {
        method: 'POST',
        body: JSON.stringify(data),
      }));
    },
    updateEndpoints: async (id: string, data: API.UpdateServiceEnvironmentEndpointsDto): Promise<API.ServiceEnvironmentDto> => {
      return json(await authorizedFetch(`/service-environments/${id}/endpoints`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }));
    },
    delete: async (id: string): Promise<void> => {
      await authorizedFetch(`/service-environments/${id}`, { method: 'DELETE' });
    },
  },

  deployments: {
    getAll: async (): Promise<API.DeploymentDto[]> => {
      return json(await authorizedFetch('/deployments'));
    },
    getById: async (id: string): Promise<API.DeploymentDto> => {
      return json(await authorizedFetch(`/deployments/${id}`));
    },
    create: async (data: API.CreateDeploymentDto): Promise<API.DeploymentDto> => {
      return json(await authorizedFetch('/deployments', {
        method: 'POST',
        body: JSON.stringify(data),
      }));
    },
    update: async (id: string, data: API.UpdateDeploymentDto): Promise<API.DeploymentDto> => {
      return json(await authorizedFetch(`/deployments/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }));
    },
    delete: async (id: string): Promise<void> => {
      await authorizedFetch(`/deployments/${id}`, { method: 'DELETE' });
    },
  },

  incidents: {
    getAll: async (): Promise<API.IncidentDto[]> => {
      return json(await authorizedFetch('/incidents'));
    },
    getActive: async (): Promise<API.IncidentDto[]> => {
      return json(await authorizedFetch('/incidents/active'));
    },
    getById: async (id: string): Promise<API.IncidentDto> => {
      return json(await authorizedFetch(`/incidents/${id}`));
    },
    create: async (data: API.CreateIncidentDto): Promise<API.IncidentDto> => {
      return json(await authorizedFetch('/incidents', {
        method: 'POST',
        body: JSON.stringify(data),
      }));
    },
    update: async (id: string, data: API.UpdateIncidentDto): Promise<API.IncidentDto> => {
      return json(await authorizedFetch(`/incidents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }));
    },
    delete: async (id: string): Promise<void> => {
      await authorizedFetch(`/incidents/${id}`, { method: 'DELETE' });
    },
  },

  // Legacy endpoint
  me: async (): Promise<any> => {
    return api.account.get();
  },

  products: {
    getAll: async (): Promise<ProductDto[]> => {
      return json(await authorizedFetch('/products'));
    },
  },

  subscriptions: {
    getActive: async (): Promise<API.SubscriptionDto | null> => {
      const res = await authorizedFetch('/subscriptions/active');
      if (res.status === 404) return null;
      return json<API.SubscriptionDto>(res, { silent: true });
    },
    getScheduled: async (): Promise<API.SubscriptionDto[]> => {
      return json(await authorizedFetch('/subscriptions/scheduled'));
    },
    create: async (data: API.CreateSubscriptionDto): Promise<API.SubscriptionResponseDto> => {
      return json(await authorizedFetch('/subscriptions', {
        method: 'POST',
        body: JSON.stringify(data),
      }));
    },
    cancel: async (): Promise<void> => {
      await authorizedFetch('/subscriptions/cancel', {
        method: 'DELETE',
      });
    },
    verify: async (reference: string): Promise<any> => {
      return json(await authorizedFetch(`/subscriptions/verify/${reference}`));
    },
  },

  // Invitations endpoints
  invitations: {
    getMyInvitations: async (): Promise<API.PendingInvitationDto[]> => {
      return json(await authorizedFetch('/invitations'));
    },
    accept: async (invitationId: string): Promise<{ message: string }> => {
      return json(await authorizedFetch(`/invitations/${invitationId}/accept`, {
        method: 'POST',
      }));
    },
    decline: async (invitationId: string): Promise<{ message: string }> => {
      return json(await authorizedFetch(`/invitations/${invitationId}/decline`, {
        method: 'POST',
      }));
    },
  },

  // Feedback endpoint
  feedback: {
    submit: async (data: { message: string; category?: string; userEmail?: string; userName?: string }): Promise<{ message: string }> => {
      return json(await authorizedFetch('/feedback', {
        method: 'POST',
        body: JSON.stringify(data),
      }));
    },
  },

  // Referral endpoints
  referral: {
    getInfo: async (): Promise<API.ReferralInfoDto> => {
      return json(await authorizedFetch('/referral/info'));
    },
    getStats: async (): Promise<API.ReferralStatsDto> => {
      return json(await authorizedFetch('/referral/stats'));
    },
    validateCode: async (code: string): Promise<API.ValidateReferralCodeDto> => {
      return json(await authorizedFetch(`/referral/validate/${code}`));
    },
  },

  // Service Monitoring Configurations
  serviceMonitoringConfigurations: {
    getAll: async (): Promise<API.ServiceMonitoringConfigurationDto[]> => {
      return json(await authorizedFetch('/monitoring-config'));
    },
    getById: async (id: string): Promise<API.ServiceMonitoringConfigurationDto> => {
      return json(await authorizedFetch(`/monitoring-config/${id}`));
    },
    getByServiceEnvironment: async (serviceEnvironmentId: string): Promise<API.ServiceMonitoringConfigurationDto[]> => {
      return json(await authorizedFetch(`/monitoring-config/service-environment/${serviceEnvironmentId}`));
    },
    create: async (data: API.CreateServiceMonitoringConfigurationDto): Promise<API.ServiceMonitoringConfigurationDto> => {
      return json(await authorizedFetch('/monitoring-config', {
        method: 'POST',
        body: JSON.stringify(data),
      }));
    },
    update: async (id: string, data: API.UpdateServiceMonitoringConfigurationDto): Promise<API.ServiceMonitoringConfigurationDto> => {
      return json(await authorizedFetch(`/monitoring-config/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }));
    },
    enable: async (id: string): Promise<{ message: string }> => {
      return json(await authorizedFetch(`/monitoring-config/${id}/enable`, {
        method: 'POST',
      }));
    },
    disable: async (id: string): Promise<{ message: string }> => {
      return json(await authorizedFetch(`/monitoring-config/${id}/disable`, {
        method: 'POST',
      }));
    },
    delete: async (id: string): Promise<void> => {
      await authorizedFetch(`/monitoring-config/${id}`, {
        method: 'DELETE',
      });
    },
    getResults: async (id: string, limit: number = 100): Promise<API.ServiceHealthResultDto[]> => {
      return json(await authorizedFetch(`/monitoring-config/${id}/results?limit=${limit}`));
    },
    getSummary: async (id: string, days: number = 7): Promise<API.ServiceMonitoringSummaryDto> => {
      return json(await authorizedFetch(`/monitoring-config/${id}/summary?days=${days}`));
    },
    getRecentFailures: async (hours: number = 24): Promise<API.ServiceHealthResultDto[]> => {
      return json(await authorizedFetch(`/monitoring-config/failures/recent?hours=${hours}`));
    },
    getLatestStatuses: async (): Promise<Record<string, API.ServiceHealthStatus>> => {
      return json(await authorizedFetch('/monitoring-config/statuses/latest'));
    },
  },

  // Audit Log
  audit: {
    getAll: async (filters?: API.AuditFiltersDto): Promise<API.AuditLogDto[]> => {
      const params = new URLSearchParams();
      if (filters) {
        if (filters.userId) params.set('userId', filters.userId);
        if (filters.action) params.set('action', filters.action);
        if (filters.resource) params.set('resource', filters.resource);
        if (filters.startDate) params.set('startDate', filters.startDate);
        if (filters.endDate) params.set('endDate', filters.endDate);
      }
      const queryString = params.toString();
      const url = `/audit${queryString ? `?${queryString}` : ''}`;
      return json(await authorizedFetch(url));
    },
    export: async (filters?: API.AuditFiltersDto, format: 'csv' | 'json' = 'csv'): Promise<string> => {
      const params = new URLSearchParams({ format });
      if (filters) {
        if (filters.userId) params.set('userId', filters.userId);
        if (filters.action) params.set('action', filters.action);
        if (filters.resource) params.set('resource', filters.resource);
        if (filters.startDate) params.set('startDate', filters.startDate);
        if (filters.endDate) params.set('endDate', filters.endDate);
      }
      const res = await authorizedFetch(`/audit/export?${params.toString()}`);
      return res.text();
    },
  },

  // API Keys
  apiKeys: {
    getAll: async (): Promise<API.ApiKeyDto[]> => {
      return json(await authorizedFetch('/api-keys'));
    },
    create: async (data: API.CreateApiKeyDto): Promise<API.CreateApiKeyResponseDto> => {
      return json(await authorizedFetch('/api-keys', {
        method: 'POST',
        body: JSON.stringify(data),
      }));
    },
    revoke: async (id: string): Promise<{ success: boolean }> => {
      return json(await authorizedFetch(`/api-keys/${id}`, {
        method: 'DELETE',
      }));
    },
  },
};

export const apiClient = api;
